/**
 * Creates a formatted full name for a user from an object containing our
 * typical user name fields.
 */
export function getFullName(
    user: {
        firstName?: string | null
        nickname?: string | null
        lastName?: string | null
    },
    alwaysShowGivenName: boolean = true
) {
    const { lastName } = user

    return `${getPreferredName(user, alwaysShowGivenName)} ${lastName}`
}

/**
 * Creates a formatted preferred first name for a user from an object containing
 * our typical user name fields.
 */
export function getPreferredName(
    user: {
        firstName?: string | null
        nickname?: string | null
    },
    alwaysShowGivenName: boolean = true
) {
    const { firstName, nickname } = user

    if (!nickname) {
        return `${firstName}`
    }

    if (alwaysShowGivenName) {
        return `${nickname} (${firstName})`
    }

    return `${nickname}`
}

/**
 * Creates a formatted set of initials for a user from an object containing our
 * typical user name fields.
 */
export function getInitials(user: {
    firstName?: string | null
    nickname?: string | null
    lastName?: string | null
}) {
    const { firstName, nickname, lastName } = user

    return nickname
        ? `${nickname[0] ?? '?'}${lastName?.[0] ?? '?'}`
        : `${firstName?.[0] ?? '?'}${lastName?.[0] ?? '?'}`
}

export function getInitialsFromPreferredName(preferredName: string) {
    const names = preferredName.split(' ')
    if (names.length === 0) return '?'
    const firstInitial = names[0][0] ?? '?'
    const lastInitial = names[names.length - 1][0] ?? '?'
    return `${firstInitial}${lastInitial}`
}

/**
 * Creates a formatted hometown string based on available city, state/province, country fields.
 */
export function getHometown(data: {
    city?: string | null
    state?: string | null
    province?: string | null
    country?: string | null
}) {
    const { city, state, province, country } = data
    const pt1 = city || ''
    const pt2 = state ? `${state}` : province ? `${province}` : ''
    const pt3 = country || ''

    // insert the appropriate commas and spacing
    if (pt1 && pt2 && pt3) return `${pt1}, ${pt2}, ${pt3}`
    if (pt1 && pt2) return `${pt1}, ${pt2}`
    if (pt2 && pt3) return `${pt2}, ${pt3}`
    if (pt1 && pt3) return `${pt1}, ${pt3}`
    return `${pt1}${pt2}${pt3}`
}
